'use client';

/**
 * LinkButton — inline text-link button primitive.
 *
 * ## What it is
 *
 * A focused `<button type="button">` that looks like inline body text but lights
 * up brand-primary on hover/focus. Inherits colour by default so it sits inline
 * in body copy without standing out until hover.
 *
 * ## When to use this vs. `<Button kind="ghost">`
 *
 * - Use `<Button kind="ghost">` for any **standalone** action button — toolbar
 *   entries, footer actions, anything with visible chrome.
 * - Use `LinkButton` only for **inline text-link** affordances inside body copy
 *   (e.g. an address inside a row, a code inside a timeline event, an "Open"
 *   link in a popover footer, a `<Plate>` chip that is also clickable).
 *
 * ## Variants
 *
 * - `'inline'` (default) — inherits colour, no padding. Brand-tint + underline
 *   on hover/focus. Use inside body paragraphs and detail rows.
 * - `'plate'` — strips button chrome so a `<Plate>` chip's own styling shows
 *   through. Brand-tint on hover. No underline.
 *
 * ## Radix `asChild` compatibility
 *
 * `LinkButton` is `forwardRef`-compatible: Radix `Popover.Trigger asChild`,
 * `Tooltip.Trigger asChild`, and `DropdownMenu.Item asChild` can all attach
 * their ref and inject `onClick` / `onPointerDown` / `data-state` / `aria-*`
 * onto the underlying `<button>` element.
 */

import React from 'react';
import styled, { css } from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';

/** Visual treatment for `LinkButton`. */
export type LinkButtonVariant = 'inline' | 'plate';

export interface LinkButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Content to render inside the button. Required. */
  children: React.ReactNode;
  /** Visual treatment. Defaults to `'inline'`. */
  variant?: LinkButtonVariant;
}

/**
 * Inline text-link button with `'inline'` and `'plate'` variants.
 * Forwards its ref for Radix `asChild` slot compatibility.
 */
export const LinkButton = React.forwardRef<HTMLButtonElement, LinkButtonProps>(
  function LinkButton(props, ref) {
    const { variant = 'inline', type = 'button', children, ...rest } = props;
    return (
      <StyledLinkButton ref={ref} type={type} $variant={variant} {...rest}>
        {children}
      </StyledLinkButton>
    );
  },
);

const inlineStyles = css`
  color: inherit;
  text-align: left;

  &:hover:not(:disabled) {
    color: ${(p) => p.theme.colors.brand.primary};
    text-decoration: underline;
  }
`;

const plateStyles = css`
  display: inline-flex;
  align-items: center;
  color: inherit;
  border-radius: ${(p) => p.theme.radius.sm};

  &:hover:not(:disabled) {
    color: ${(p) => p.theme.colors.brand.primary};
  }
`;

const StyledLinkButton = styled.button<{ $variant: LinkButtonVariant }>`
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  ${(p) => (p.$variant === 'plate' ? plateStyles : inlineStyles)}

  ${focusRing}
`;
